import React, { useMemo } from "react";

import SystematicRepechageMatchCard from "./SystematicRepechageMatchCard";

const CARD_W = 340;
const CARD_H = 178;
const ROUND_GAP = 132;
const ROW_GAP = 42;
const TOP_OFFSET = 92;
const PAD_X = 28;
const PAD_BOTTOM = 50;

const getAccentClasses = (accent) => {
  if (accent === "violet") {
    return {
      badge:
        "bg-violet-100 text-violet-700 ring-violet-200 dark:bg-violet-500/15 dark:text-violet-100 dark:ring-violet-400/30",
      border: "border-violet-200 dark:border-violet-400/20",
    };
  }

  return {
    badge:
      "bg-blue-100 text-blue-700 ring-blue-200 dark:bg-blue-500/15 dark:text-blue-100 dark:ring-blue-400/30",
    border: "border-blue-200 dark:border-blue-400/20",
  };
};

const computeLayout = (rounds = []) => {
  const matchMap = new Map();
  const roundsWithPosition = [];

  const maxFirstRoundCount = Math.max(
    1,
    ...rounds.map((round) => round.matches.length || 1),
  );

  const boardWidth =
    PAD_X * 2 + rounds.length * CARD_W + Math.max(0, rounds.length - 1) * ROUND_GAP;

  const baseStep = CARD_H + ROW_GAP;

  rounds.forEach((round, roundIndex) => {
    const positionedMatches = round.matches.map((match, matchIndex) => {
      const x = PAD_X + roundIndex * (CARD_W + ROUND_GAP);

      let y = TOP_OFFSET + matchIndex * baseStep;

      const sourceYs = [match.source1, match.source2]
        .filter(Boolean)
        .map((key) => matchMap.get(key))
        .filter(Boolean)
        .map((pos) => pos.y + CARD_H / 2);

      if (sourceYs.length) {
        const avg = sourceYs.reduce((sum, value) => sum + value, 0) / sourceYs.length;
        y = avg - CARD_H / 2;
      }

      const position = {
        x,
        y,
      };

      matchMap.set(match.key, position);

      return {
        ...match,
        visualRoundIndex: roundIndex,
        visualMatchIndex: matchIndex,
        x,
        y,
      };
    });

    roundsWithPosition.push({
      ...round,
      visualRoundIndex: roundIndex,
      matches: positionedMatches,
    });
  });

  const maxY = Math.max(
    TOP_OFFSET + CARD_H,
    ...roundsWithPosition.flatMap((round) =>
      round.matches.map((match) => match.y + CARD_H),
    ),
  );

  const boardHeight = Math.max(
    TOP_OFFSET + maxFirstRoundCount * baseStep + PAD_BOTTOM,
    maxY + PAD_BOTTOM,
  );

  return {
    boardWidth,
    boardHeight,
    rounds: roundsWithPosition,
    matchMap,
  };
};

const Connectors = ({ rounds, matchMap, width, height }) => {
  const paths = [];

  rounds.forEach((round) => {
    round.matches.forEach((match) => {
      [match.source1, match.source2].filter(Boolean).forEach((sourceKey) => {
        const source = matchMap.get(sourceKey);
        const target = matchMap.get(match.key);

        if (!source || !target) return;

        const fromX = source.x + CARD_W;
        const fromY = source.y + CARD_H / 2;
        const toX = target.x;
        const toY = target.y + CARD_H / 2;
        const midX = fromX + (toX - fromX) / 2;

        paths.push({
          key: `${sourceKey}-${match.key}`,
          d: `M ${fromX} ${fromY} H ${midX} V ${toY} H ${toX}`,
          completed: !!match.winnerId || !!match.autoWinner,
        });
      });
    });
  });

  return (
    <svg
      className="pointer-events-none absolute inset-0 z-0"
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      aria-hidden="true"
    >
      {paths.map((path) => (
        <path
          key={path.key}
          d={path.d}
          fill="none"
          stroke={path.completed ? "#22c55e" : "#cbd5e1"}
          strokeWidth={path.completed ? 3 : 2}
          strokeLinecap="round"
          strokeLinejoin="round"
          opacity={path.completed ? 0.95 : 0.75}
        />
      ))}
    </svg>
  );
};

const RoundHeader = ({ round, x }) => (
  <div
    className="absolute top-6"
    style={{
      left: x,
      width: CARD_W,
    }}
  >
    <div className="flex items-start justify-between gap-3">
      <div>
        <h4 className="text-base font-bold tracking-tight text-slate-900 dark:text-white">
          {round.title}
        </h4>
        <p className="mt-0.5 text-xs font-semibold uppercase tracking-[0.16em] text-slate-400 dark:text-slate-500">
          {round.matches.length} combat{round.matches.length > 1 ? "s" : ""}
        </p>
      </div>

      <div className="flex h-9 w-9 items-center justify-center rounded-2xl bg-slate-100 text-xs font-bold text-slate-600 ring-1 ring-slate-200 dark:bg-white/10 dark:text-slate-300 dark:ring-white/10">
        {round.matches.length}
      </div>
    </div>
  </div>
);

const SystematicRepechageBracket = ({
  title,
  subtitle,
  accent = "blue",
  data,
  onOpenCreate,
  onOpenEdit,
  onOpenScoreboard,
}) => {
  const rounds = data?.rounds || [];
  const accentClasses = getAccentClasses(accent);

  const layout = useMemo(() => computeLayout(rounds), [rounds]);

  return (
    <div
      className={`overflow-hidden rounded-[1.8rem] border bg-white shadow-[0_14px_40px_rgba(15,23,42,0.07)] dark:bg-slate-950 ${accentClasses.border}`}
    >
      <div className="border-b border-slate-200 px-5 py-4 dark:border-white/10">
        <div
          className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-bold uppercase tracking-[0.14em] ring-1 ${accentClasses.badge}`}
        >
          {title}
        </div>

        <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
          {subtitle}
        </p>
      </div>

      {!rounds.length ? (
        <div className="p-5 text-sm text-slate-500 dark:text-slate-400">
          Le tableau de repêchage sera généré dès que les premiers combats du
          tableau principal seront saisis.
        </div>
      ) : (
        <div className="overflow-auto bg-[radial-gradient(circle_at_top_left,rgba(59,130,246,0.08),transparent_32%),linear-gradient(180deg,#f8fafc,#eef2f7)] p-4 dark:bg-[radial-gradient(circle_at_top_left,rgba(59,130,246,0.12),transparent_32%),linear-gradient(180deg,#020617,#0f172a)]">
          <div
            className="relative rounded-[1.5rem] border border-white/80 bg-white/80 backdrop-blur dark:border-white/10 dark:bg-slate-950/70"
            style={{
              width: layout.boardWidth,
              minWidth: layout.boardWidth,
              height: layout.boardHeight,
            }}
          >
            <Connectors
              rounds={layout.rounds}
              matchMap={layout.matchMap}
              width={layout.boardWidth}
              height={layout.boardHeight}
            />

            {layout.rounds.map((round) => {
              const x = PAD_X + round.visualRoundIndex * (CARD_W + ROUND_GAP);

              return (
                <RoundHeader
                  key={`header-${round.visualRoundIndex}`}
                  round={round}
                  x={x}
                />
              );
            })}

            <div className="relative z-10">
              {layout.rounds.map((round) =>
                round.matches.map((match) => (
                  <div
                    key={match.key}
                    className="absolute"
                    style={{
                      left: match.x,
                      top: match.y,
                      width: CARD_W,
                      height: CARD_H,
                    }}
                  >
                    <SystematicRepechageMatchCard
                      match={match}
                      cardHeight={CARD_H}
                      onOpenCreate={onOpenCreate}
                      onOpenEdit={onOpenEdit}
                      onOpenScoreboard={onOpenScoreboard}
                    />
                  </div>
                )),
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SystematicRepechageBracket;